import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Plus, Edit2, Trash2, X, AlertCircle } from '../../icons/wp-icons';
import { werkzeugApi } from '../../api/werkzeugApi';

const ZUSTAND_LABELS = {
  NEU: 'Neu',
  GEBRAUCHT: 'Gebraucht',
  DEFEKT: 'Defekt',
  GELEAST: 'Geleast',
  AUSSORTIERT: 'Aussortiert'
};

const ZUSTAND_COLORS = {
  NEU: 'bg-green-100 text-green-800',
  GEBRAUCHT: 'bg-blue-100 text-blue-800',
  DEFEKT: 'bg-red-100 text-red-800',
  GELEAST: 'bg-purple-100 text-purple-800',
  AUSSORTIERT: 'bg-gray-100 text-gray-600'
};

function formatDate(value) {
  if (!value) return '–';
  return new Date(value).toLocaleDateString('de-DE');
}

function daysUntil(value) {
  if (!value) return null;
  const ms = new Date(value).getTime() - Date.now();
  return Math.round(ms / (1000 * 60 * 60 * 24));
}

function WartungBadge({ datum }) {
  if (!datum) return null;
  const days = daysUntil(datum);
  let cls = 'bg-gray-100 text-gray-600';
  if (days < 0) cls = 'bg-red-100 text-red-800';
  else if (days <= 30) cls = 'bg-amber-100 text-amber-800';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full ${cls}`}>
      {days < 0 && <AlertCircle className="w-3 h-3" />}
      Wartung: {formatDate(datum)}
    </span>
  );
}

function toDateInput(value) {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function WerkzeugModal({ werkzeug, onClose, onSaved }) {
  const isEdit = !!werkzeug;
  const [form, setForm] = useState({
    bezeichnung: werkzeug?.bezeichnung || '',
    kategorie: werkzeug?.kategorie || '',
    seriennummer: werkzeug?.seriennummer || '',
    zustand: werkzeug?.zustand || 'NEU',
    naechsteWartung: toDateInput(werkzeug?.naechsteWartung),
    kaufdatum: toDateInput(werkzeug?.kaufdatum),
    kaufpreis: werkzeug?.kaufpreis ?? '',
    bemerkung: werkzeug?.bemerkung || ''
  });
  const [saving, setSaving] = useState(false);

  const setField = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.bezeichnung.trim()) {
      toast.error('Bezeichnung ist Pflicht');
      return;
    }
    try {
      setSaving(true);
      const payload = {
        ...form,
        kaufpreis: form.kaufpreis === '' ? null : Number(form.kaufpreis),
        naechsteWartung: form.naechsteWartung || null,
        kaufdatum: form.kaufdatum || null
      };
      if (isEdit) {
        await werkzeugApi.update(werkzeug.id, payload);
        toast.success('Werkzeug aktualisiert');
      } else {
        await werkzeugApi.create(payload);
        toast.success('Werkzeug angelegt');
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold">{isEdit ? 'Werkzeug bearbeiten' : 'Neues Werkzeug'}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Bezeichnung *</label>
              <input value={form.bezeichnung} onChange={setField('bezeichnung')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Bohrhammer Hilti TE 30" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Kategorie</label>
              <input value={form.kategorie} onChange={setField('kategorie')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Elektro, Hand, Sicherheit" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Zustand</label>
              <select value={form.zustand} onChange={setField('zustand')} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                {Object.entries(ZUSTAND_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Seriennummer</label>
              <input value={form.seriennummer} onChange={setField('seriennummer')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nächste Wartung</label>
              <input type="date" value={form.naechsteWartung} onChange={setField('naechsteWartung')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Kaufdatum</label>
              <input type="date" value={form.kaufdatum} onChange={setField('kaufdatum')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Kaufpreis (€)</label>
              <input type="number" step="0.01" value={form.kaufpreis} onChange={setField('kaufpreis')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Bemerkung</label>
              <textarea value={form.bemerkung} onChange={setField('bemerkung')} className="w-full px-3 py-2 border border-gray-300 rounded-lg" rows={2} />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Abbrechen</button>
            <button type="submit" disabled={saving} className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50">
              {saving ? 'Speichere…' : (isEdit ? 'Speichern' : 'Anlegen')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function WerkzeugeTab() {
  const [werkzeuge, setWerkzeuge] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editWerkzeug, setEditWerkzeug] = useState(null);
  const [filterZustand, setFilterZustand] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const data = await werkzeugApi.getAll(filterZustand ? { zustand: filterZustand } : {});
      setWerkzeuge(data);
    } catch (err) {
      console.error(err);
      toast.error('Fehler beim Laden der Werkzeuge');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filterZustand]);

  const handleDelete = async (id) => {
    try {
      await werkzeugApi.delete(id);
      toast.success('Werkzeug gelöscht');
      setDeleteConfirmId(null);
      await load();
    } catch (err) {
      toast.error('Löschen fehlgeschlagen');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <select value={filterZustand} onChange={(e) => setFilterZustand(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
          <option value="">Alle Zustände</option>
          {Object.entries(ZUSTAND_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <button onClick={() => { setEditWerkzeug(null); setShowModal(true); }} className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-button font-medium hover:bg-primary-dark">
          <Plus className="w-5 h-5" /> Neues Werkzeug
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Lade…</div>
        ) : werkzeuge.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Noch keine Werkzeuge angelegt.</div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase">
              <tr>
                <th className="px-4 py-3">Bezeichnung</th>
                <th className="px-4 py-3">Kategorie</th>
                <th className="px-4 py-3">Seriennr.</th>
                <th className="px-4 py-3">Zustand</th>
                <th className="px-4 py-3">Wartung</th>
                <th className="px-4 py-3 text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {werkzeuge.map(w => (
                <tr key={w.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium">{w.bezeichnung}</div>
                    {w.bemerkung && <div className="text-xs text-gray-500 line-clamp-1">{w.bemerkung}</div>}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{w.kategorie || '–'}</td>
                  <td className="px-4 py-3 text-sm text-gray-500 font-mono">{w.seriennummer || '–'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded-full ${ZUSTAND_COLORS[w.zustand] || 'bg-gray-100 text-gray-600'}`}>
                      {ZUSTAND_LABELS[w.zustand] || w.zustand}
                    </span>
                  </td>
                  <td className="px-4 py-3"><WartungBadge datum={w.naechsteWartung} /></td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => { setEditWerkzeug(w); setShowModal(true); }} className="p-1.5 text-gray-500 hover:text-primary hover:bg-gray-100 rounded">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => setDeleteConfirmId(w.id)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-gray-100 rounded">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <WerkzeugModal werkzeug={editWerkzeug} onClose={() => setShowModal(false)} onSaved={() => { setShowModal(false); load(); }} />
      )}

      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md p-6">
            <h3 className="text-lg font-semibold mb-2">Werkzeug löschen?</h3>
            <p className="text-sm text-gray-600 mb-4">Diese Aktion kann nicht rückgängig gemacht werden.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeleteConfirmId(null)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Abbrechen</button>
              <button onClick={() => handleDelete(deleteConfirmId)} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Löschen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
